import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import { api } from '@/lib/api/client'
import { parseRepresentationMarkdown } from '@/lib/honcho/parseRepresentation'
import { theme } from '@/lib/theme'

const WORKING_REPRESENTATION_CACHE_KEY = 'working_representation_cache'
const CACHE_EXPIRATION_MS = 30 * 60 * 1000 // 30 minutes

interface CachedData {
  value: any
  timestamp: number
}

interface WorkingRepresentationAnimatedProps {
  disabled?: boolean
}

/**
 * WorkingRepresentationAnimated widget that displays the size of the user's working representation
 */
export default function WorkingRepresentationAnimated({ disabled = false }: WorkingRepresentationAnimatedProps) {
  const { t } = useTranslation()
  const { colors } = useThemeMode()
  const { user, isLoading: isUserLoading } = useUser()
  const [workingRepresentation, setWorkingRepresentation] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isMinimized, setIsMinimized] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const programStateRef = useRef<{ workingRepresentation: any }>({
    workingRepresentation: null
  })

  const fetchWorkingRepresentation = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const { data, error } = await api.api.profile['working-representation'].get()

      if (error) throw new Error('Failed to fetch working representation')

      // Honcho v3 SDK returns a markdown string, parse it into structured format
      const rawRepresentation = data.workingRepresentation
      const parsedRepresentation = typeof rawRepresentation === 'string'
        ? parseRepresentationMarkdown(rawRepresentation)
        : rawRepresentation

      setWorkingRepresentation(parsedRepresentation)

      // Cache in localStorage with timestamp
      const cachedData: CachedData = {
        value: workingRepresentation,
        timestamp: Date.now(),
      }
      localStorage.setItem(
        WORKING_REPRESENTATION_CACHE_KEY,
        JSON.stringify(cachedData)
      )
    } catch (err) {
      console.error('Error fetching working representation:', err)
      setError('Failed to load working representation')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // Don't fetch data if user is not authenticated
    if (!user) {
      setIsLoading(false)
      return
    }

    // Load from cache first, checking expiration
    const cachedRaw = localStorage.getItem(WORKING_REPRESENTATION_CACHE_KEY)

    let validData: any = null
    if (cachedRaw) {
      try {
        const cached: CachedData = JSON.parse(cachedRaw)
        const isExpired = Date.now() - cached.timestamp > CACHE_EXPIRATION_MS
        if (!isExpired) {
          validData = cached.value
        }
      } catch {
        // Invalid cache format, treat as missing
      }
    }

    if (validData) {
      // Parse cached data if it's a string (old format)
      const parsedData = typeof validData === 'string'
        ? parseRepresentationMarkdown(validData)
        : validData
      setWorkingRepresentation(parsedData)
      setIsLoading(false)
    } else {
      fetchWorkingRepresentation()
    }
  }, [user, fetchWorkingRepresentation])

  // Listen for refresh events from authenticated chat turns
  useEffect(() => {
    if (!user) return

    const handleRefreshEvent = () => {
      fetchWorkingRepresentation()
    }

    window.addEventListener('working-representation-refresh', handleRefreshEvent)

    return () => {
      window.removeEventListener('working-representation-refresh', handleRefreshEvent)
    }
  }, [user, fetchWorkingRepresentation])

  // Update program state when working representation changes
  useEffect(() => {
    programStateRef.current.workingRepresentation = workingRepresentation
  }, [workingRepresentation])

  // Determine if widget should be minimized (either by user or when disabled)
  const effectivelyMinimized = disabled || isMinimized

  // Initialize run.js program
  useEffect(() => {
    // Don't initialize if minimized - wait until expanded
    if (!canvasRef.current || !user || isLoading || effectivelyMinimized) return

    let mounted = true

    const initProgram = async () => {
      try {
        // Dynamically import the run function and program
        const { run } = await import('@/lib/play/run.js')
        const program = await import('@/lib/programs/working-memory-viz.js')

        if (!mounted || !canvasRef.current) return

        // Run the program
        await run(program, {
          element: canvasRef.current,
          renderer: 'canvas',
          cols: 28,
          rows: 12,
          fps: 24,
          backgroundColor: colors.primaryBg,
          color: colors.primaryText,
          primaryAccent: colors.primaryAccent,
          visualAccent1: colors.visualAccent1,
          visualAccent2: colors.visualAccent2,
          visualAccent3: colors.visualAccent3,
          visualAccent4: colors.visualAccent4,
          primaryText: colors.primaryText,
          secondaryText: colors.secondaryText,
          fontFamily: theme.fontFamily.mono,
          fontSize: '11px',
          lineHeight: '1.2',
        }, programStateRef.current)
      } catch (err) {
        console.error('Failed to initialize visualization:', err)
      }
    }

    initProgram()

    return () => {
      mounted = false
    }
  }, [user, isLoading, effectivelyMinimized, colors.primaryBg, colors.primaryText, colors.primaryAccent, colors.visualAccent1, colors.visualAccent2, colors.visualAccent3, colors.visualAccent4, colors.secondaryText])

  const handleRefresh = () => {
    fetchWorkingRepresentation()
  }

  // Don't render if user is not authenticated
  if (isUserLoading || !user) {
    return null
  }

  if (error) {
    return (
      <div
        style={{
          backgroundColor: colors.primaryBg,
          border: `1px solid ${colors.primaryBorder}`,
          borderRadius: theme.borderRadius.md,
          padding: theme.spacing.md,
          boxShadow: theme.shadows.sm,
          width: '220px',
        }}
      >
        <div
          style={{
            fontSize: theme.fontSize.xs,
            color: colors.errorText || colors.secondaryText,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        backgroundColor: colors.primaryBg,
        border: `1px solid ${colors.primaryBorder}`,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.md,
        boxShadow: theme.shadows.sm,
        transition: theme.transitions.boxShadow,
        cursor: 'default',
        width: '220px',
        position: 'relative',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = theme.shadows.md
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = theme.shadows.sm
        }
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: effectivelyMinimized ? 0 : theme.spacing.sm,
        }}
      >
        <div
          onClick={() => !disabled && setIsMinimized(!isMinimized)}
          style={{
            fontSize: theme.fontSize.sm,
            fontWeight: 'bold',
            color: colors.primaryAccent,
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
          }}
          title={disabled ? 'Open representation window to interact' : (isMinimized ? 'Expand' : 'Minimize')}
        >
          {t('navigation.representation')}
        </div>
        {!effectivelyMinimized && (
          <button
            onClick={handleRefresh}
            disabled={isLoading || disabled}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primaryAccent,
              cursor: (isLoading || disabled) ? 'not-allowed' : 'pointer',
              padding: '2px 6px',
              fontSize: theme.fontSize.xs,
              opacity: (isLoading || disabled) ? 0.5 : 1,
            }}
            title="Refresh working representation"
          >
            ↻
          </button>
        )}
      </div>

      {/* Canvas for animated visualization */}
      {!effectivelyMinimized && (
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: '160px',
            borderRadius: theme.borderRadius.sm,
            backgroundColor: colors.secondaryBg,
          }}
        />
      )}
    </div>
  )
}
